import { Ship, Download, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Drawer } from '../../../components/ui/Drawer';
import { Badge } from '../../../components/ui/Badge';
import { MODE_KEY, TRANSPORT, STATUS_PROGRESS, STEP_CONFIG } from '../utils';
import { StepItem } from './StepItem';
import { DocCard } from './DocCard';

export function ShipmentDrawer({ row, onClose, companyName }) {
  const { t } = useTranslation();
  if (!row) return null;

  const modeKey = MODE_KEY[row.mode] || 'reefer';
  const { Icon: ModeIcon, bg: modeBg, text: modeText } = TRANSPORT[modeKey] || TRANSPORT.reefer;
  const statusLabel = row.statusKey ? t(`shipments.${row.statusKey}`) : '—';
  const progress    = STATUS_PROGRESS[row.status] ?? 50;
  const { doneCount, activeIdx } = STEP_CONFIG[row.status] ?? { doneCount: 0, activeIdx: null };

  const stepDefs = [
    { key: 'booking',   label: t('shipments.drawer_step_booking')   },
    { key: 'stuffing',  label: t('shipments.drawer_step_stuffing')  },
    { key: 'departure', label: t('shipments.drawer_step_departure') },
    { key: 'transit',   label: t('shipments.drawer_step_transit')   },
    { key: 'customs',   label: t('shipments.drawer_step_customs')   },
    { key: 'delivery',  label: t('shipments.drawer_step_delivery')  },
  ];

  const steps = stepDefs.map((def, i) => {
    let status   = 'pending';
    let sublabel = t('shipments.drawer_scheduled');
    if (i < doneCount) {
      status   = 'done';
      sublabel = row.etd || '—';
    } else if (i === activeIdx) {
      status   = 'active';
      sublabel = t('shipments.drawer_in_progress');
    }
    return { ...def, status, sublabel };
  });

  const originLabel = row.originCity
    ? `${row.originCity}${row.originCountryCode ? ` (${row.originCountryCode})` : ''}`
    : row.departure || '—';

  const techFields = [
    { label: t('shipments.drawer_container'),     value: row.containerRef },
    { label: t('shipments.drawer_qty'),           value: row.quantityTons != null ? `${row.quantityTons} t` : null },
    { label: t('shipments.drawer_temp_label'),    value: row.temperatureSetPoint != null ? `+${row.temperatureSetPoint}°C` : null },
    { label: t('shipments.drawer_incoterm'),      value: row.incoterm },
    { label: t('shipments.drawer_insurance'),     value: null },
    { label: t('shipments.drawer_invoice_label'), value: null },
    { label: t('shipments.drawer_merch_value'),   value: null },
  ].filter((f) => f.value != null);

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width="w-[620px]"
      headerContent={
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[13px] font-semibold text-slate-900">
              {row.containerRef || t('shipments.no_ref')}
            </span>
            <Badge variant={row.statusVariant}>{statusLabel}</Badge>
            <span className={`inline-flex items-center justify-center w-5 h-5 rounded ${modeBg} ${modeText}`}>
              <ModeIcon size={11} strokeWidth={1.75} />
            </span>
          </div>
          {(companyName || row.productName) && (
            <p className="text-[12px] text-slate-500 mt-0.5">
              {[companyName, row.productName].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      }
      footer={
        <div className="flex items-center justify-between w-full">
          <button className="text-[12.5px] text-primary font-medium hover:underline">
            {t('shipments.drawer_contact_manager')}
          </button>
          <div className="flex gap-2">
            <button className="h-8 px-3 flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white text-[12px] font-medium text-slate-600 hover:bg-slate-50 transition">
              <Download size={13} strokeWidth={1.75} />
              {t('shipments.drawer_docs_btn')}
            </button>
            <button className="h-8 px-3 flex items-center gap-1.5 rounded-lg bg-accent text-white text-[12px] font-medium hover:bg-accent-light transition">
              <Ship size={13} strokeWidth={1.75} />
              {t('shipments.drawer_freshtrack_btn')}
            </button>
          </div>
        </div>
      }
    >
      {(row.statusGroup === 'incident' || row.incident?.description) && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-rose-100 border border-rose-200 flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertCircle size={17} strokeWidth={1.75} className="text-rose-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-bold text-rose-900 mb-0.5">
                {t('shipments.drawer_incident_detected')}
              </p>
              <p className="text-[12.5px] text-rose-700 leading-snug">
                {row.incident?.description || t('shipments.drawer_incident_hint')}
              </p>
              <button className="mt-3 h-8 px-3 rounded-lg border border-rose-400 text-[12px] font-semibold text-rose-600 bg-white hover:bg-rose-50 transition">
                {t('shipments.drawer_incident_declare')}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
            {t('shipments.drawer_origin_label')}
          </p>
          <p className="text-[15px] font-semibold text-slate-900">{originLabel}</p>
          <p className="text-[12px] text-slate-500 mt-1">ETD : {row.etd || '—'}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">
            {t('shipments.drawer_destination_label')}
          </p>
          <p className="text-[15px] font-semibold text-slate-900">{row.arrival || '—'}</p>
          <p className="text-[12px] text-slate-500 mt-1">ETA : —</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[13px] font-semibold text-slate-900">{t('shipments.drawer_tracking')}</p>
          <span className="flex items-center gap-1.5 text-[12px] text-emerald-600 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
            {t('shipments.drawer_freshtrack_active')}
          </span>
        </div>
        <div className="flex items-center justify-between text-[12px] text-slate-500 mb-2">
          <span>{originLabel !== '—' ? originLabel.split(' (')[0] : '—'}</span>
          <span className="font-semibold text-slate-900">{progress}%</span>
          <span>{row.arrival || '—'}</span>
        </div>
        <div className="relative h-2 bg-slate-100 rounded-full" style={{ overflow: 'visible' }}>
          <div className="absolute h-full bg-primary rounded-full" style={{ width: `${progress}%` }} />
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-6 h-6 bg-primary rounded-full flex items-center justify-center shadow-sm"
            style={{ left: `${progress}%` }}
          >
            <Ship size={11} strokeWidth={1.5} className="text-white" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 items-start">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[13px] font-semibold text-slate-900 mb-4">{t('shipments.drawer_steps')}</p>
          {steps.map((step, i) => (
            <StepItem
              key={step.key}
              label={step.label}
              sublabel={step.sublabel}
              status={step.status}
              isLast={i === steps.length - 1}
            />
          ))}
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[13px] font-semibold text-slate-900 mb-4">{t('shipments.drawer_tech_details')}</p>
          <div className="divide-y divide-slate-100">
            {techFields.map(({ label, value }) => (
              <div key={label} className="flex items-center justify-between gap-2 py-2.5">
                <span className="text-[12px] text-slate-500 flex-shrink-0">{label}</span>
                <span className="text-[12.5px] font-semibold text-slate-900 text-right">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {row.documents?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-100">
            <p className="text-[13px] font-semibold text-slate-900">{t('shipments.drawer_docs_section')}</p>
            <button className="flex items-center gap-1.5 text-[12px] font-semibold text-primary hover:underline">
              {t('shipments.drawer_docs_download_all')}
              <Download size={12} strokeWidth={2} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {row.documents.map((doc, i) => (
              <DocCard key={i} doc={doc} index={i} />
            ))}
          </div>
        </div>
      )}
    </Drawer>
  );
}
